import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'

import { AlertTriangle } from 'lucide-react'
import { NotFoundContent } from '../not-found-content'
import { useGetCampaigns } from 'features/campaigns/campaigns_api'
import { useAuth } from 'context/authentication/useAuth'
import {
  SequenceWizardSidebar,
  type WizardStep,
} from 'features/campaigns/SequenceWizardSidebar'
import { WizardSequenceStep } from 'features/campaigns/wizard-steps/WizardSequenceStep'
import { WizardSettingsStep } from 'features/campaigns/wizard-steps/WizardSettingsStep'
import { WizardReviewLeadsStep } from 'features/campaigns/wizard-steps/WizardReviewLeadsStep'
import { WizardLaunchStep } from 'features/campaigns/wizard-steps/WizardLaunchStep'
import { useGetCampaignSequence } from 'features/campaigns/sequences_api'
import { useGetCampaignLaunchReadiness } from 'features/campaigns/campaign_insights_api'
import { useGetCampaignSettings } from 'features/campaigns/campaign_settings_api'
import { useEntityBreadcrumbs, useHideFooter } from 'context/layout'
import { WORKFLOW_STATUS } from 'models/campaign'

const STEPS_READ_ONLY: WizardStep[] = ['sequence']
const STEPS_NO_CONTACTS: WizardStep[] = ['review-leads', 'sequence', 'settings']
const STEPS_WITH_CONTACTS: WizardStep[] = [
  'review-leads',
  'sequence',
  'settings',
  'launch',
]

export function SequenceWizardPageV2() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const hasBisonWorkspace = user?.company?.has_bison_workspace ?? false

  const [currentStep, setCurrentStep] = useState<WizardStep>('sequence')
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStep>>(
    new Set()
  )
  const [hasInitializedCurrentStep, setHasInitializedCurrentStep] =
    useState(false)

  const { data: campaigns, isLoading } = useGetCampaigns()
  const campaignId = parseInt(id ?? '-1')
  const { data: sequence } = useGetCampaignSequence(campaignId, true)
  const campaign = campaigns?.results.find((c) => c.id === parseInt(id ?? '-1'))
  const { data: settings, isLoading: settingsLoading } = useGetCampaignSettings(
    campaignId,
    !!campaign
  )
  const { data: readiness, isLoading: readinessLoading } =
    useGetCampaignLaunchReadiness(campaignId, !isLoading)

  const hasContacts = (campaign?.contact_count ?? 0) > 0
  const campaignStatus = settings?.status || campaign?.workflow_status
  const isLaunchedCampaign =
    campaignStatus === 'launching' ||
    campaignStatus === 'queued' ||
    campaignStatus === 'active' ||
    campaignStatus === 'paused' ||
    campaignStatus === 'completed' ||
    campaignStatus === 'archived' ||
    campaignStatus === 'deleted' ||
    campaign?.workflow_status === WORKFLOW_STATUS.ACTIVE ||
    campaign?.workflow_status === WORKFLOW_STATUS.PAUSED ||
    campaign?.workflow_status === WORKFLOW_STATUS.COMPLETE
  const isReadOnlySequenceView = isLaunchedCampaign

  const breadcrumbs = useMemo(
    () =>
      campaign
        ? [
            {
              label: campaign.name,
              path: `/campaigns/${id}/${isLaunchedCampaign ? 'dashboard' : 'detail'}`,
            },
            { label: isLaunchedCampaign ? 'Sequence' : 'Sequence Wizard' },
          ]
        : [
            { label: 'Campaigns', path: '/campaigns' },
            { label: 'Sequence Wizard' },
          ],
    [campaign, id, isLaunchedCampaign]
  )
  useEntityBreadcrumbs(breadcrumbs)
  useHideFooter()
  const wizardSteps = useMemo(
    () =>
      isReadOnlySequenceView
        ? STEPS_READ_ONLY
        : hasContacts
          ? STEPS_WITH_CONTACTS
          : STEPS_NO_CONTACTS,
    [hasContacts, isReadOnlySequenceView]
  )
  const settingsComplete =
    !!sequence &&
    (readiness?.sender_emails.is_complete ?? false) &&
    (readiness?.schedule.is_complete ?? false)

  const persistedCompletedSteps = useMemo(() => {
    const steps = new Set<WizardStep>()

    if (sequence) {
      if (hasContacts) {
        steps.add('review-leads')
      }
      steps.add('sequence')
    }

    if (settingsComplete) {
      steps.add('settings')
    }

    return steps
  }, [hasContacts, sequence, settingsComplete])

  const allCompletedSteps = useMemo(
    () =>
      new Set([
        ...Array.from(completedSteps),
        ...Array.from(persistedCompletedSteps),
      ]),
    [completedSteps, persistedCompletedSteps]
  )
  const accessibleSteps = useMemo(() => {
    if (isReadOnlySequenceView) {
      return new Set<WizardStep>(['sequence'])
    }

    const steps = new Set<WizardStep>(['review-leads', 'sequence'])

    if (sequence) {
      steps.add('settings')
    }

    if (hasContacts && readiness?.all_complete) {
      steps.add('launch')
    }

    return steps
  }, [hasContacts, isReadOnlySequenceView, readiness?.all_complete, sequence])

  const recommendedStep: WizardStep = useMemo(() => {
    if (isReadOnlySequenceView) {
      return 'sequence'
    }
    if (!sequence) {
      return 'review-leads'
    }
    if (readiness?.all_complete) {
      return 'launch'
    }
    if (!settingsComplete) {
      return 'settings'
    }
    return 'settings'
  }, [
    isReadOnlySequenceView,
    readiness?.all_complete,
    sequence,
    settingsComplete,
  ])

  useEffect(() => {
    setCurrentStep('sequence')
    setCompletedSteps(new Set())
    setHasInitializedCurrentStep(false)
  }, [campaignId])

  useEffect(() => {
    if (isLoading || readinessLoading || !campaign || isReadOnlySequenceView) {
      return
    }

    if (!hasInitializedCurrentStep) {
      setCurrentStep(recommendedStep)
      setHasInitializedCurrentStep(true)
      return
    }

    if (
      !wizardSteps.includes(currentStep) ||
      (currentStep === 'launch' && !readiness?.all_complete)
    ) {
      setCurrentStep(recommendedStep)
    }
  }, [
    campaign,
    currentStep,
    hasInitializedCurrentStep,
    isLoading,
    isReadOnlySequenceView,
    readiness?.all_complete,
    readinessLoading,
    recommendedStep,
    wizardSteps,
  ])

  if (isLoading || readinessLoading || settingsLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner animation="border" />
      </div>
    )
  }

  if (!campaign) {
    return <NotFoundContent />
  }

  const resolvedCurrentStep = isReadOnlySequenceView
    ? 'sequence'
    : wizardSteps.includes(currentStep)
      ? currentStep
      : recommendedStep

  const currentStepIndex = wizardSteps.indexOf(resolvedCurrentStep)
  const isLastStep = currentStepIndex === wizardSteps.length - 1

  const goToNext = () => {
    if (!isLastStep) {
      setCompletedSteps((prev) => new Set([...prev, resolvedCurrentStep]))
      setCurrentStep(wizardSteps[currentStepIndex + 1])
    }
  }

  const goToPrev = () => {
    if (currentStepIndex === 0) {
      navigate(
        `/campaigns/${id}/${isReadOnlySequenceView ? 'dashboard' : 'detail'}`
      )
      return
    }
    setCurrentStep(wizardSteps[currentStepIndex - 1])
  }

  const handleStepClick = (step: WizardStep) => {
    if (step === 'launch') {
      if (hasContacts && readiness?.all_complete) {
        setCurrentStep(step)
      }
      return
    }

    setCurrentStep(step)
  }

  return (
    <div className="flex flex-1 overflow-hidden">
      <SequenceWizardSidebar
        currentStep={resolvedCurrentStep}
        completedSteps={allCompletedSteps}
        accessibleSteps={accessibleSteps}
        campaign={campaign}
        hasContacts={hasContacts}
        onStepClick={handleStepClick}
        visibleStepsOverride={wizardSteps}
        title={isReadOnlySequenceView ? 'Sequence' : 'Sequence Wizard'}
      />

      <main className="flex flex-1 flex-col overflow-auto bg-surface-alt">
        {!hasBisonWorkspace ? (
          <div className="flex flex-1 items-center justify-center p-8">
            <div className="flex max-w-md flex-col items-center gap-4 rounded-lg border border-line bg-surface p-8 text-center">
              <AlertTriangle className="size-10 text-amber-500" />
              <h3 className="text-lg font-semibold text-fg">
                Email Outreach Not Configured
              </h3>
              <p className="text-sm text-fg-secondary">
                Your company needs an email outreach workspace before you can
                build sequences. Please contact your admin or set up outreach in
                Settings &gt; Email Outreach.
              </p>
              <button
                onClick={() => navigate(`/campaigns/${id}/detail`)}
                className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand/90"
              >
                Back to Campaign
              </button>
            </div>
          </div>
        ) : resolvedCurrentStep === 'sequence' ? (
          <WizardSequenceStep
            campaignId={campaign.id}
            campaignName={campaign.name}
            onBack={goToPrev}
            onNext={goToNext}
            stepNumber={wizardSteps.indexOf('sequence') + 1}
            totalSteps={wizardSteps.length}
            readOnly={isReadOnlySequenceView}
            backLabel={isReadOnlySequenceView ? 'Back to Dashboard' : 'Back'}
          />
        ) : resolvedCurrentStep === 'settings' ? (
          <WizardSettingsStep
            campaignId={campaign.id}
            onNext={goToNext}
            onBack={goToPrev}
            hasContacts={hasContacts}
            stepNumber={wizardSteps.indexOf('settings') + 1}
            totalSteps={wizardSteps.length}
            onSaveExit={() => navigate(`/campaigns/${id}/detail`)}
          />
        ) : resolvedCurrentStep === 'review-leads' ? (
          <WizardReviewLeadsStep
            campaignId={campaign.id}
            onBack={goToPrev}
            onNext={goToNext}
          />
        ) : resolvedCurrentStep === 'launch' ? (
          <WizardLaunchStep
            campaignId={campaign.id}
            campaign={campaign}
            onBack={goToPrev}
            onLaunch={() => navigate(`/campaigns/${id}/dashboard`)}
            stepNumber={wizardSteps.indexOf('launch') + 1}
            totalSteps={wizardSteps.length}
          />
        ) : null}
      </main>
    </div>
  )
}
